import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useContainersStore } from '@/deploy/stores/containers.store';
import {
  containerReleasesService,
  type ContainerRelease,
  type CreateReleaseInput,
} from '@/deploy/services/container-releases.service';
import { containerFilesService, type FileModificationRemote } from '@/deploy/services/container-files.service';
import { resourcePacksService, type ResourcePack } from '@/deploy/services/resource-packs.service';
import { cdnConfigService, type CdnConfig } from '@/deploy/services/cdn-config.service';
import type { MinecraftContainer, ArtifactRelease, EnvironmentId } from '@/deploy/types';
import { formatRelative } from '@/deploy/utils/format';
import styles from './Deploy.module.scss';

// Env cible des releases lancées depuis cette page. Pour l'instant : preprod
// (Phase 4 ajoutera dev/beta quand Ptero sera câblé).
const DEPLOY_TARGET_ENV: EnvironmentId = 'preprod';

// ============================================================
// Catégorisation visuelle des bumps d'artefacts
// ============================================================
type ArtifactGroup = 'mod' | 'rp' | 'bp' | 'other';

function inferGroup(a: ArtifactRelease): ArtifactGroup {
  const n = (a.name || '').toLowerCase();
  if (n.includes('resourcepack') || n.includes('texturepack')) return 'rp';
  if (n.includes('behaviorpack') || n.includes('behaviourpack')) return 'bp';
  if (a.kind === 'mod' || a.kind === 'module' || a.kind === 'plugin' || a.kind === 'lib') return 'mod';
  // Heuristique : nom finissant en "Mod" / "Plugin" / "Lib" → mod
  if (/(?:mod|plugin|lib)$/i.test(a.name || '')) return 'mod';
  return 'other';
}

const GROUP_LABEL: Record<ArtifactGroup, string> = {
  mod: 'Mods & plugins',
  rp: 'Resource Packs (embarqués)',
  bp: 'Behavior Packs (embarqués)',
  other: 'Autres artefacts',
};

const GROUP_ICON: Record<ArtifactGroup, 'zap' | 'package' | 'sparkles' | 'settings'> = {
  mod: 'zap',
  rp: 'sparkles',
  bp: 'package',
  other: 'settings',
};

// ============================================================
// Page /deploy — Release multi-containers en parallèle
// ============================================================
// Objectif : un panneau qui liste tous les containers du projet,
// permet de cocher ceux à releaser, et fire les releases en parallèle
// avec auto-curation + auto-merge activés par défaut.
// ============================================================

interface PerContainerState {
  // état de chargement et données
  active: ContainerRelease | null;
  history: ContainerRelease[];
  pendingMods: FileModificationRemote[];
  loaded: boolean;

  // plan de release (modifiable par l'utilisateur)
  included: boolean;
  targetVersion: string;
  acceptedBumps: Map<string, string>; // artifactName → targetVersion
  includedModPaths: Set<string>;

  // résultat après fire
  firing: boolean;
  fired: ContainerRelease | null;
  error: string | null;
}

function nextPatch(version: string | null | undefined, history: ContainerRelease[]): string {
  const candidates: string[] = [];
  if (version) candidates.push(version);
  history.forEach((r) => candidates.push(r.branchName));
  const parsed = candidates
    .map((v) => v.match(/^v?(\d+)\.(\d+)\.(\d+)$/))
    .filter((m): m is RegExpMatchArray => m !== null)
    .map((m) => [Number(m[1]), Number(m[2]), Number(m[3])] as const);
  if (parsed.length === 0) return 'v0.0.1';
  parsed.sort((a, b) => (b[0] - a[0]) || (b[1] - a[1]) || (b[2] - a[2]));
  const [maj, min, patch] = parsed[0];
  return `v${maj}.${min}.${patch + 1}`;
}

export function Deploy() {
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());
  const containers = useContainersStore((s) => s.containers);
  const loadedProjectId = useContainersStore((s) => s.loadedProjectId);
  const load = useContainersStore((s) => s.load);

  // States par container (clé = container.code)
  const [states, setStates] = useState<Record<string, PerContainerState>>({});
  const [autoApplyGlobal, setAutoApplyGlobal] = useState(true);
  const [autoMergeGlobal, setAutoMergeGlobal] = useState(true);
  const [commitMessageGlobal, setCommitMessageGlobal] = useState('');
  const [globalFiring, setGlobalFiring] = useState(false);

  // ====== Resource Packs à pousser sur CDN preprod ======
  const [projectRps, setProjectRps] = useState<ResourcePack[]>([]);
  const [selectedRpIds, setSelectedRpIds] = useState<Set<string>>(new Set());
  const [preprodCdn, setPreprodCdn] = useState<CdnConfig | null>(null);
  const [rpDeployResults, setRpDeployResults] = useState<Record<string, { ok: boolean; skipped: boolean; error?: string }>>({});

  useEffect(() => {
    if (!project.id) return;
    void (async () => {
      try {
        const [rps, cdn] = await Promise.all([
          resourcePacksService.list(project.id),
          cdnConfigService.get(project.id, DEPLOY_TARGET_ENV).catch(() => null),
        ]);
        setProjectRps(rps);
        // Préselection : tous les packs cochés par défaut (uploadés au prochain deploy)
        setSelectedRpIds(new Set(rps.map((r) => r.id)));
        setPreprodCdn(cdn);
      } catch {
        // best effort — section RP juste cachée si la liste ne charge pas
      }
    })();
  }, [project.id]);

  const toggleRp = (id: string) => {
    setSelectedRpIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const cdnReady = !!preprodCdn && preprodCdn.enabled
    && !!preprodCdn.sftpHost && !!preprodCdn.sftpUser
    && (!!preprodCdn.sftpPrivateKey || !!preprodCdn.sftpPassword);

  // Liste filtrée des containers du projet
  const projectContainers = useMemo(
    () => containers.filter((c) => c.projectId === project.id),
    [containers, project.id],
  );

  // Charger les containers au mount
  useEffect(() => {
    if (!project.id) return;
    void load(project.id);
  }, [project.id, load]);

  // Charger l'état (active release + mods) de chaque container quand la liste arrive
  const refreshContainer = useCallback(async (c: MinecraftContainer) => {
    try {
      const [active, history, mods] = await Promise.all([
        containerReleasesService.active(project.id, c.code),
        containerReleasesService.list(project.id, c.code, 10),
        containerFilesService.listMods(project.id, c.code).catch(() => [] as FileModificationRemote[]),
      ]);
      setStates((prev) => {
        const existing = prev[c.code];
        // Si l'utilisateur a déjà fait des modifs, on ne touche pas à son plan
        if (existing?.loaded) {
          return { ...prev, [c.code]: { ...existing, active, history, pendingMods: mods } };
        }
        const suggested = nextPatch(c.containerVersion, history);
        const availableBumps = c.releases.filter(
          (r) => r.latestAvailable && r.latestAvailable !== r.version,
        );
        const hasSomethingToRelease = availableBumps.length > 0 || mods.length > 0;
        return {
          ...prev,
          [c.code]: {
            active,
            history,
            pendingMods: mods,
            loaded: true,
            // Préselection : on coche si pas bloqué ET qu'il y a qqch à release
            included: !active && hasSomethingToRelease,
            targetVersion: suggested,
            acceptedBumps: new Map(availableBumps.map((b) => [b.name, b.latestAvailable!])),
            includedModPaths: new Set(mods.map((m) => m.path)),
            firing: false,
            fired: null,
            error: null,
          },
        };
      });
    } catch {
      // Best effort
    }
  }, [project.id]);

  useEffect(() => {
    if (loadedProjectId !== project.id) return;
    projectContainers.forEach((c) => { void refreshContainer(c); });
  }, [loadedProjectId, project.id, projectContainers, refreshContainer]);

  const updateState = (code: string, patch: Partial<PerContainerState>) => {
    setStates((prev) => {
      const cur = prev[code];
      if (!cur) return prev;
      return { ...prev, [code]: { ...cur, ...patch } };
    });
  };

  const toggleInclude = (code: string) => {
    setStates((prev) => {
      const cur = prev[code];
      if (!cur) return prev;
      return { ...prev, [code]: { ...cur, included: !cur.included } };
    });
  };

  const toggleBump = (code: string, name: string, latest: string) => {
    setStates((prev) => {
      const cur = prev[code];
      if (!cur) return prev;
      const bumps = new Map(cur.acceptedBumps);
      if (bumps.has(name)) bumps.delete(name); else bumps.set(name, latest);
      return { ...prev, [code]: { ...cur, acceptedBumps: bumps } };
    });
  };

  const setBumpVersion = (code: string, name: string, ver: string) => {
    setStates((prev) => {
      const cur = prev[code];
      if (!cur) return prev;
      const bumps = new Map(cur.acceptedBumps);
      bumps.set(name, ver);
      return { ...prev, [code]: { ...cur, acceptedBumps: bumps } };
    });
  };

  const toggleMod = (code: string, path: string) => {
    setStates((prev) => {
      const cur = prev[code];
      if (!cur) return prev;
      const paths = new Set(cur.includedModPaths);
      if (paths.has(path)) paths.delete(path); else paths.add(path);
      return { ...prev, [code]: { ...cur, includedModPaths: paths } };
    });
  };

  const selectAll = () => {
    setStates((prev) => {
      const next: Record<string, PerContainerState> = { ...prev };
      for (const c of projectContainers) {
        const s = next[c.code];
        if (s && !s.active && !s.fired) {
          next[c.code] = { ...s, included: true };
        }
      }
      return next;
    });
  };

  const selectNone = () => {
    setStates((prev) => {
      const next: Record<string, PerContainerState> = { ...prev };
      for (const c of projectContainers) {
        const s = next[c.code];
        if (s) next[c.code] = { ...s, included: false };
      }
      return next;
    });
  };

  // Liste des containers sélectionnés et validés
  const selectedCodes = useMemo(
    () => projectContainers
      .filter((c) => {
        const s = states[c.code];
        return s && s.included && !s.active && !s.fired;
      })
      .map((c) => c.code),
    [projectContainers, states],
  );

  const fireAll = async () => {
    if (selectedCodes.length === 0) return;
    setGlobalFiring(true);
    // Marquer toutes les sélections en firing
    setStates((prev) => {
      const next = { ...prev };
      for (const code of selectedCodes) {
        if (next[code]) next[code] = { ...next[code], firing: true, error: null };
      }
      return next;
    });

    // Fire en parallèle, mais on traite les résultats individuellement
    const results = await Promise.all(selectedCodes.map(async (code) => {
      const s = states[code];
      if (!s) return { code, ok: false };
      try {
        const payload: CreateReleaseInput = {
          newVersion: s.targetVersion,
          includedFileModPaths: Array.from(s.includedModPaths),
          commitMessage: commitMessageGlobal.trim() || undefined,
        };
        if (autoApplyGlobal) {
          payload.autoApply = {
            bumps: Array.from(s.acceptedBumps.entries()).map(([name, toVersion]) => ({ name, toVersion })),
            autoMerge: autoMergeGlobal,
            commitMessage: commitMessageGlobal.trim() || undefined,
          };
        }
        const created = await containerReleasesService.create(project.id, code, payload);
        updateState(code, { firing: false, fired: created });
        return { code, ok: true };
      } catch (e) {
        updateState(code, { firing: false, error: e instanceof Error ? e.message : 'Erreur inconnue' });
        return { code, ok: false };
      }
    }));

    // === Push des Resource Packs sélectionnés sur le CDN preprod ===
    // En parallèle, indépendant des container releases : si la liste est vide ou
    // CDN pas configuré, le backend renvoie skipped (warning) → on log dans
    // rpDeployResults pour affichage. Échec RP ≠ échec release.
    const selectedRps = projectRps.filter((r) => selectedRpIds.has(r.id));
    const rpResults: Record<string, { ok: boolean; skipped: boolean; error?: string }> = {};
    await Promise.all(selectedRps.map(async (rp) => {
      try {
        const res = await resourcePacksService.deploy(project.id, rp.id, {
          environments: [DEPLOY_TARGET_ENV],
        });
        const entry = res.results.find((r) => r.environment === DEPLOY_TARGET_ENV);
        rpResults[rp.id] = {
          ok: entry?.ok ?? false,
          skipped: entry?.skipped ?? false,
          error: entry?.error || undefined,
        };
      } catch (e) {
        rpResults[rp.id] = { ok: false, skipped: false, error: e instanceof Error ? e.message : 'KO' };
      }
    }));
    setRpDeployResults(rpResults);

    setGlobalFiring(false);

    // Si au moins une release container a été créée, on redirige vers le suivi.
    // Petit délai pour que le user voie le statut "Lancée" avant le saut.
    if (results.some((r) => r.ok)) {
      setTimeout(() => navigate('/deploiement/deploy/progress'), 1200);
    }
  };

  // Stats globales
  const totalContainers = projectContainers.length;
  const totalBlocked = projectContainers.filter((c) => states[c.code]?.active).length;
  const totalFired = projectContainers.filter((c) => states[c.code]?.fired).length;
  const totalSelected = selectedCodes.length;

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Releases multi-containers"
        title="Releaser plusieurs containers à la fois"
        subtitle="Sélectionne les containers, ajuste les versions, et lance toutes les releases en parallèle. L'auto-curation + auto-merge sont activés par défaut."
        actions={
          <div className={styles.headerActions}>
            <Button variant="ghost" size="sm" onClick={selectNone} disabled={globalFiring}>Tout décocher</Button>
            <Button variant="secondary" size="sm" onClick={selectAll} disabled={globalFiring} leftIcon={<Icon name="check" size={12} />}>
              Tout cocher (sauf bloqués)
            </Button>
          </div>
        }
      />

      <section className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryNum}>{totalContainers}</span>
          <em>containers</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.summaryNum, totalSelected > 0 && styles.numHl].filter(Boolean).join(' ')}>{totalSelected}</span>
          <em>sélectionnés</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.summaryNum, totalBlocked > 0 && styles.numWarn].filter(Boolean).join(' ')}>{totalBlocked}</span>
          <em>bloqués (release en cours)</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.summaryNum, totalFired > 0 && styles.numOk].filter(Boolean).join(' ')}>{totalFired}</span>
          <em>lancées</em>
        </div>
      </section>

      <section className={styles.globalConfig}>
        <h3>Options globales</h3>
        <label className={styles.globalRow}>
          <input
            type="checkbox"
            checked={autoApplyGlobal}
            onChange={(e) => setAutoApplyGlobal(e.target.checked)}
            disabled={globalFiring}
          />
          <span>
            <strong>Auto-curation</strong> — applique automatiquement les bumps cochés après <code>update.yml</code>
          </span>
        </label>
        {autoApplyGlobal && (
          <label className={styles.globalRow}>
            <input
              type="checkbox"
              checked={autoMergeGlobal}
              onChange={(e) => setAutoMergeGlobal(e.target.checked)}
              disabled={globalFiring}
            />
            <span>
              <strong>Auto-merge</strong> — merge la PR automatiquement après application
            </span>
          </label>
        )}
        <label className={styles.globalField}>
          <span>Message de commit (appliqué à toutes les releases)</span>
          <input
            type="text"
            value={commitMessageGlobal}
            onChange={(e) => setCommitMessageGlobal(e.target.value)}
            placeholder="ex: bump artefacts du sprint"
            disabled={globalFiring}
          />
        </label>
      </section>

      {/* ======= Section Resource Packs CDN ======= */}
      {projectRps.length > 0 && (
        <section className={styles.globalConfig}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="sparkles" size={16} />
            Resource Packs à pousser sur le CDN ({DEPLOY_TARGET_ENV})
            <Badge tone={cdnReady ? 'success' : 'warning'}>
              {cdnReady ? 'CDN configuré' : 'CDN non configuré'}
            </Badge>
          </h3>
          {!cdnReady && (
            <div style={{
              padding: '8px 12px',
              background: 'var(--color-warning-bg)',
              color: 'var(--color-warning)',
              borderRadius: 6,
              fontSize: 12,
            }}>
              <Icon name="warn" size={12} /> L'env <strong>{DEPLOY_TARGET_ENV}</strong> n'a pas de credentials CDN.
              Les uploads seront <strong>skippés</strong> avec un warning, sans bloquer le déploiement. Configure via Settings → CDN.
            </div>
          )}
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
            On re-upload systématiquement les packs sélectionnés (qu'ils aient bumpé ou pas) sur le CDN de cet env, pour garder le client en cohérence. Décoche ceux que tu ne veux pas pousser cette fois-ci.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            {projectRps.map((rp) => {
              const checked = selectedRpIds.has(rp.id);
              const ver = rp.latestAvailable || rp.currentVersion;
              const result = rpDeployResults[rp.id];
              return (
                <label key={rp.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 10px',
                  background: 'var(--color-background-secondary)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  opacity: globalFiring ? 0.6 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleRp(rp.id)}
                    disabled={globalFiring}
                    style={{ accentColor: 'var(--color-primary)' }}
                  />
                  <strong style={{ flex: 1 }}>{rp.name}</strong>
                  <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {ver ?? '—'}
                  </code>
                  {result?.ok && <Badge tone="success"><Icon name="check" size={10} /> Uploadé</Badge>}
                  {result?.skipped && <Badge tone="warning"><Icon name="warn" size={10} /> Skip ({result.error})</Badge>}
                  {result && !result.ok && !result.skipped && <Badge tone="danger"><Icon name="warn" size={10} /> KO {result.error}</Badge>}
                </label>
              );
            })}
          </div>
        </section>
      )}

      <section className={styles.containerList}>
        {projectContainers.length === 0 && (
          <div className={styles.empty}>
            {loadedProjectId === project.id
              ? 'Aucun container dans ce projet.'
              : 'Chargement des containers…'}
          </div>
        )}
        {projectContainers.map((c) => {
          const s = states[c.code];
          if (!s) {
            return (
              <article key={c.id} className={styles.containerCard}>
                <header className={styles.cardHeader}>
                  <span className={styles.chip} style={{ background: c.color }}>
                    <Icon name="package" size={14} />
                  </span>
                  <strong>{c.displayName}</strong>
                  <span className={styles.muted}>Chargement…</span>
                </header>
              </article>
            );
          }
          return (
            <ContainerCard
              key={c.id}
              container={c}
              state={s}
              disabled={globalFiring}
              onToggleInclude={() => toggleInclude(c.code)}
              onChangeVersion={(v) => updateState(c.code, { targetVersion: v })}
              onToggleBump={(name, latest) => toggleBump(c.code, name, latest)}
              onChangeBumpVersion={(name, v) => setBumpVersion(c.code, name, v)}
              onToggleMod={(path) => toggleMod(c.code, path)}
              onOpenContainer={() => navigate(`/deploiement/containers/${c.code}`)}
            />
          );
        })}
      </section>

      <section className={styles.fireBar}>
        <Button
          variant="primary"
          size="lg"
          leftIcon={<Icon name="rocket" size={16} />}
          onClick={() => void fireAll()}
          loading={globalFiring}
          disabled={totalSelected === 0 || globalFiring}
        >
          {totalSelected === 0
            ? 'Coche au moins un container'
            : `Lancer ${totalSelected} release${totalSelected > 1 ? 's' : ''} en parallèle`}
        </Button>
        {totalFired > 0 && (
          <p className={styles.fireInfo}>
            <Icon name="check" size={12} /> {totalFired} release{totalFired > 1 ? 's' : ''} lancée{totalFired > 1 ? 's' : ''} — suis la progression dans la page de chaque container.
          </p>
        )}
      </section>
    </div>
  );
}

// ============================================================
// Card : 1 container
// ============================================================
interface ContainerCardProps {
  container: MinecraftContainer;
  state: PerContainerState;
  disabled: boolean;
  onToggleInclude: () => void;
  onChangeVersion: (v: string) => void;
  onToggleBump: (name: string, latest: string) => void;
  onChangeBumpVersion: (name: string, v: string) => void;
  onToggleMod: (path: string) => void;
  onOpenContainer: () => void;
}

function ContainerCard({
  container, state, disabled,
  onToggleInclude, onChangeVersion,
  onToggleBump, onChangeBumpVersion, onToggleMod,
  onOpenContainer,
}: ContainerCardProps) {
  const availableBumps = container.releases.filter(
    (r) => r.latestAvailable && r.latestAvailable !== r.version,
  );
  const validVer = /^v\d+\.\d+\.\d+$/.test(state.targetVersion);

  const isBlocked = !!state.active;
  const isFired = !!state.fired;
  const hasError = !!state.error;
  const nothingToRelease = availableBumps.length === 0 && state.pendingMods.length === 0;

  const statusBadge = (() => {
    if (state.firing) return <Badge tone="info">Lancement…</Badge>;
    if (isFired) return <Badge tone="success"><Icon name="check" size={11} /> Lancée</Badge>;
    if (hasError) return <Badge tone="danger"><Icon name="warn" size={11} /> Échec</Badge>;
    if (isBlocked) return <Badge tone="warning">Release en cours ({state.active!.status})</Badge>;
    if (nothingToRelease) return <Badge tone="muted">Rien à release</Badge>;
    return <Badge tone="info">{availableBumps.length} bumps · {state.pendingMods.length} mods</Badge>;
  })();

  return (
    <article className={[
      styles.containerCard,
      state.included && !isBlocked && !isFired && styles.cardSelected,
      isBlocked && styles.cardBlocked,
      isFired && styles.cardFired,
      hasError && styles.cardError,
    ].filter(Boolean).join(' ')}>
      <header className={styles.cardHeader}>
        {!isBlocked && !isFired && (
          <input
            type="checkbox"
            checked={state.included}
            onChange={onToggleInclude}
            disabled={disabled || nothingToRelease}
            className={styles.cardCheck}
            title={nothingToRelease ? 'Rien à release' : 'Inclure dans la release'}
          />
        )}
        <span className={styles.chip} style={{ background: container.color }}>
          <Icon name="package" size={14} />
        </span>
        <div className={styles.cardTitle}>
          <strong>{container.displayName}</strong>
          <code className={styles.cardVer}>{container.containerVersion ?? '—'}</code>
        </div>
        {statusBadge}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="external" size={11} />} onClick={onOpenContainer}>
          Détail
        </Button>
      </header>

      {isBlocked && (
        <div className={styles.blockedInfo}>
          <Icon name="warn" size={14} />
          <span>
            Une release <code>{state.active!.branchName}</code> est déjà en cours pour ce container.
            Termine-la (ou annule-la) avant de relancer.
          </span>
        </div>
      )}

      {hasError && (
        <div className={styles.errorBox}>
          <Icon name="warn" size={14} /> {state.error}
        </div>
      )}

      {isFired && state.fired && (
        <div className={styles.firedInfo}>
          <Icon name="rocket" size={14} />
          <span>
            Branche <code>{state.fired.branchName}</code> poussée à {formatRelative(state.fired.initiatedAt)} — status: <strong>{state.fired.status}</strong>
          </span>
        </div>
      )}

      {!isBlocked && !isFired && state.included && (
        <div className={styles.cardBody}>
          <div className={styles.row}>
            <label className={styles.fieldShort}>
              <span>Version cible</span>
              <input
                type="text"
                value={state.targetVersion}
                onChange={(e) => onChangeVersion(e.target.value)}
                disabled={disabled}
                className={!validVer ? styles.inputError : ''}
              />
            </label>
            <div className={styles.rowStats}>
              {availableBumps.length > 0 && (
                <Badge tone="info">{state.acceptedBumps.size}/{availableBumps.length} bumps</Badge>
              )}
              {state.pendingMods.length > 0 && (
                <Badge tone="warning">{state.includedModPaths.size}/{state.pendingMods.length} mods fichiers</Badge>
              )}
            </div>
          </div>

          {availableBumps.length > 0 && (
            <details className={styles.section}>
              <summary>
                <Icon name="zap" size={12} /> Bumps d'artefacts ({state.acceptedBumps.size}/{availableBumps.length})
              </summary>
              {/* Groupé par type (mods / RP / BP / autres) pour la lisibilité */}
              {(['mod', 'rp', 'bp', 'other'] as ArtifactGroup[]).map((group) => {
                const groupBumps = availableBumps.filter((b) => inferGroup(b) === group);
                if (groupBumps.length === 0) return null;
                return (
                  <div key={group} style={{ marginTop: 8 }}>
                    <div style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 4,
                    }}>
                      <Icon name={GROUP_ICON[group]} size={11} />
                      {GROUP_LABEL[group]} ({groupBumps.length})
                    </div>
                    <div className={styles.bumpList}>
                      {groupBumps.map((b) => {
                        const checked = state.acceptedBumps.has(b.name);
                        const target = state.acceptedBumps.get(b.name) ?? b.latestAvailable!;
                        return (
                          <label key={b.name} className={styles.bumpRow}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => onToggleBump(b.name, b.latestAvailable!)}
                              disabled={disabled}
                            />
                            <span className={styles.bumpName}>{b.name}</span>
                            <span className={styles.bumpVer}>
                              <code className={styles.dim}>{b.version}</code>
                              <Icon name="arrow-right" size={10} />
                              <input
                                type="text"
                                value={target}
                                disabled={!checked || disabled}
                                onChange={(e) => onChangeBumpVersion(b.name, e.target.value)}
                                className={styles.versionInput}
                              />
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </details>
          )}

          {state.pendingMods.length > 0 && (
            <details className={styles.section}>
              <summary>
                <Icon name="settings" size={12} /> Modifications de fichiers ({state.includedModPaths.size}/{state.pendingMods.length})
              </summary>
              <div className={styles.modList}>
                {state.pendingMods.map((m) => (
                  <label key={m.path} className={styles.modRow}>
                    <input
                      type="checkbox"
                      checked={state.includedModPaths.has(m.path)}
                      onChange={() => onToggleMod(m.path)}
                      disabled={disabled}
                    />
                    <Badge tone={m.type === 'added' ? 'success' : m.type === 'modified' ? 'warning' : 'danger'}>
                      {m.type}
                    </Badge>
                    <code className={styles.modPath}>{m.path}</code>
                    {m.by && <span className={styles.modBy}>par {m.by}</span>}
                  </label>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </article>
  );
}
